import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { VendorSpendPanel } from '@/components/vendors/VendorSpendPanel';
import React from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  getVendors,
  createVendor,
  updateVendor,
  deleteVendor,
  type Vendor,
} from '@/lib/api/vendors';
import { LargeTitleHeader, PullToRefresh, SwipeableRow } from '@/components/common';
import type { SwipeAction } from '@/components/common';

interface VendorFormData {
  name: string;
  contact: string;
  phone: string;
  email: string;
  address: string;
  website: string;
  notes: string;
}

const EMPTY_FORM: VendorFormData = {
  name: '', contact: '', phone: '', email: '',
  address: '', website: '', notes: '',
};

const vendorToForm = (v: Vendor): VendorFormData => ({
  name: v.name ?? '',
  contact: v.contact ?? '',
  phone: v.phone ?? '',
  email: v.email ?? '',
  address: v.address ?? '',
  website: v.website ?? '',
  notes: v.notes ?? '',
});

// ── Shared form fields ────────────────────────────────────────────────────────

interface FormFieldsProps {
  form: VendorFormData;
  onChange: (field: keyof VendorFormData, value: string) => void;
  disabled?: boolean;
}

const VendorFormFields = ({ form, onChange, disabled }: FormFieldsProps) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div className="sm:col-span-2">
      <label className="block text-xs font-semibold text-gray-600 mb-1">
        Name <span className="text-red-500">*</span>
      </label>
      <input
        type="text"
        value={form.name}
        onChange={e => onChange('name', e.target.value)}
        disabled={disabled}
        placeholder="Vendor name"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Contact Person</label>
      <input
        type="text"
        value={form.contact}
        onChange={e => onChange('contact', e.target.value)}
        disabled={disabled}
        placeholder="John Smith"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Phone</label>
      <input
        type="tel"
        value={form.phone}
        onChange={e => onChange('phone', e.target.value)}
        disabled={disabled}
        placeholder="(250) 555-0100"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Email</label>
      <input
        type="email"
        value={form.email}
        onChange={e => onChange('email', e.target.value)}
        disabled={disabled}
        placeholder="vendor@example.com"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
    </div>
    <div>
      <label className="block text-xs font-semibold text-gray-600 mb-1">Website</label>
      <input
        type="url"
        value={form.website}
        onChange={e => onChange('website', e.target.value)}
        disabled={disabled}
        placeholder="https://example.com"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
    </div>
    <div className="sm:col-span-2">
      <label className="block text-xs font-semibold text-gray-600 mb-1">Address</label>
      <input
        type="text"
        value={form.address}
        onChange={e => onChange('address', e.target.value)}
        disabled={disabled}
        placeholder="Street address"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60"
      />
    </div>
    <div className="sm:col-span-2">
      <label className="block text-xs font-semibold text-gray-600 mb-1">Notes</label>
      <textarea
        value={form.notes}
        onChange={e => onChange('notes', e.target.value)}
        disabled={disabled}
        rows={3}
        placeholder="Additional notes…"
        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-60 resize-none"
      />
    </div>
  </div>
);

// ── Edit Modal ─────────────────────────────────────────────────────────────────

interface EditModalProps {
  vendor: Vendor;
  onSave: (id: number, data: VendorFormData) => Promise<void>;
  onClose: () => void;
}

const EditModal = ({ vendor, onSave, onClose }: EditModalProps) => {
  const [form, setForm] = useState<VendorFormData>(vendorToForm(vendor));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (field: keyof VendorFormData, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      await onSave(vendor.id, form);
      onClose();
    } catch {
      setError('Failed to save vendor.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-gray-900">Edit Vendor</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <span className="material-symbols-outlined text-xl">close</span>
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{error}</div>
          )}
          <VendorFormFields form={form} onChange={handleChange} disabled={saving} />
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────────

export const VendorsPage = () => {
  const navigate = useNavigate();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState<VendorFormData>(EMPTY_FORM);
  const [addSaving, setAddSaving] = useState(false);
  const [addError, setAddError] = useState('');
  const [editVendor, setEditVendor] = useState<Vendor | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<'name' | 'materials'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setVendors(await getVendors());
    } catch {
      setError('Failed to load vendors.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAddChange = (field: keyof VendorFormData, value: string) => {
    setAddForm(prev => ({ ...prev, [field]: value }));
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!addForm.name.trim()) { setAddError('Name is required.'); return; }
    setAddSaving(true);
    setAddError('');
    try {
      await createVendor({
        name: addForm.name.trim(),
        contact: addForm.contact || undefined,
        phone: addForm.phone || undefined,
        email: addForm.email || undefined,
        address: addForm.address || undefined,
        website: addForm.website || undefined,
        notes: addForm.notes || undefined,
      });
      setAddForm(EMPTY_FORM);
      setShowAddForm(false);
      await load();
    } catch {
      setAddError('Failed to create vendor.');
    } finally {
      setAddSaving(false);
    }
  };

  const handleEditSave = async (id: number, form: VendorFormData) => {
    await updateVendor(id, {
      name: form.name.trim(),
      contact: form.contact || undefined,
      phone: form.phone || undefined,
      email: form.email || undefined,
      address: form.address || undefined,
      website: form.website || undefined,
      notes: form.notes || undefined,
    });
    await load();
  };

  const toggleSort = (field: 'name' | 'materials') => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const visibleVendors = vendors
    .filter(v => !search || v.name.toLowerCase().includes(search.toLowerCase()) || (v.contact || '').toLowerCase().includes(search.toLowerCase()) || (v.email || '').toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      let av: string | number = 0, bv: string | number = 0;
      if (sortField === 'name') { av = a.name.toLowerCase(); bv = b.name.toLowerCase(); }
      else { av = a.materials_count ?? a.materialsCount ?? 0; bv = b.materials_count ?? b.materialsCount ?? 0; }
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });

  const handleExportVendors = () => {
    const header = ['Name', 'Contact', 'Phone', 'Email', 'Address', 'Website', 'Materials'];
    const rows = visibleVendors.map(v => [
      `"${(v.name || '').replace(/"/g, '""')}"`,
      `"${(v.contact || '').replace(/"/g, '""')}"`,
      v.phone || '',
      v.email || '',
      `"${(v.address || '').replace(/"/g, '""')}"`,
      v.website || '',
      v.materials_count ?? v.materialsCount ?? 0,
    ].join(','));
    const blob = new Blob([[header.join(','), ...rows].join('\n')], { type: 'text/csv' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `vendors-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  const handleDelete = async (id: number) => {
    setDeleting(true);
    try {
      await deleteVendor(id);
      setDeleteId(null);
      await load();
    } catch {
      setError('Failed to delete vendor.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* iOS Large Title Header */}
        <LargeTitleHeader
          title="Vendors"
          subtitle="Resources"
          actions={
            <button
              onClick={() => { setShowAddForm(v => !v); setAddError(''); }}
              className="flex items-center gap-1.5 h-9 px-3 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
            >
              <span className="material-symbols-outlined text-base">
                {showAddForm ? 'close' : 'add'}
              </span>
              <span className="hidden sm:inline">{showAddForm ? 'Cancel' : 'Add Vendor'}</span>
            </button>
          }
        />
      <VendorSpendPanel />

        {error && (
          <div className="flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm font-medium text-red-700">{error}</p>
            <button onClick={load} className="text-sm font-bold text-red-700 hover:underline">Retry</button>
          </div>
        )}

        {/* Add vendor form */}
        {showAddForm && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="text-base font-bold text-gray-900 mb-4">New Vendor</h2>
            {addError && (
              <div className="mb-4 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700">{addError}</div>
            )}
            <form onSubmit={handleAddSubmit} className="space-y-4">
              <VendorFormFields form={addForm} onChange={handleAddChange} disabled={addSaving} />
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => { setShowAddForm(false); setAddForm(EMPTY_FORM); setAddError(''); }}
                  disabled={addSaving}
                  className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addSaving}
                  className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 disabled:opacity-60 transition-colors"
                >
                  {addSaving ? 'Creating…' : 'Create Vendor'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Search + export */}
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-gray-400 text-base">search</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search vendors…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <button
            onClick={handleExportVendors}
            title="Export to CSV"
            className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 bg-white text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
          >
            <span className="material-symbols-outlined text-base">download</span>
            <span className="hidden sm:inline">CSV</span>
          </button>
        </div>

        {/* Vendors table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-sm text-gray-400">
              <span className="material-symbols-outlined animate-spin text-3xl text-gray-300 block mx-auto mb-2">progress_activity</span>
              Loading vendors…
            </div>
          ) : vendors.length === 0 ? (
            <div className="p-12 text-center">
              <span className="material-symbols-outlined text-5xl text-gray-300 block mb-3">store</span>
              <p className="text-sm font-medium text-gray-500 mb-3">No vendors yet</p>
              <button
                onClick={() => setShowAddForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors"
              >
                <span className="material-symbols-outlined text-base">add</span>
                Add Your First Vendor
              </button>
            </div>
          ) : (
            <PullToRefresh onRefresh={load}>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide">
                      <th className="text-left px-5 py-3 font-semibold cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('name')}>
                        <span className="flex items-center gap-1">Name {sortField === 'name' && <span className="material-symbols-outlined text-xs">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</span>
                      </th>
                      <th className="text-left px-5 py-3 font-semibold hidden sm:table-cell">Contact</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Phone</th>
                      <th className="text-left px-5 py-3 font-semibold hidden md:table-cell">Email</th>
                      <th className="text-right px-5 py-3 font-semibold hidden sm:table-cell cursor-pointer select-none hover:text-gray-700" onClick={() => toggleSort('materials')}>
                        <span className="flex items-center justify-end gap-1">Materials {sortField === 'materials' && <span className="material-symbols-outlined text-xs">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}</span>
                      </th>
                      <th className="px-5 py-3" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {visibleVendors.map(vendor => (
                      <tr
                        key={vendor.id}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                        onClick={() => navigate(`/vendors/${vendor.id}`)}
                      >
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">{vendor.name}</p>
                          {vendor.address && (
                            <p className="text-xs text-gray-400 truncate max-w-[200px]">{vendor.address}</p>
                          )}
                        </td>
                        <td className="px-5 py-3.5 text-gray-600 hidden sm:table-cell">
                          {vendor.contact || '—'}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          {vendor.phone ? (
                            <a href={`tel:${vendor.phone}`} onClick={e => e.stopPropagation()} className="text-primary hover:underline">
                              {vendor.phone}
                            </a>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-5 py-3.5 hidden md:table-cell">
                          {vendor.email ? (
                            <a href={`mailto:${vendor.email}`} onClick={e => e.stopPropagation()} className="text-primary hover:underline truncate block max-w-[180px]">
                              {vendor.email}
                            </a>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-5 py-3.5 text-right text-gray-600 hidden sm:table-cell">
                          {(vendor.materials_count ?? vendor.materialsCount ?? 0) > 0
                            ? vendor.materials_count ?? vendor.materialsCount
                            : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center justify-end gap-1" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setEditVendor(vendor)} className="p-1.5 rounded-lg text-gray-400 hover:text-primary hover:bg-primary/10 transition-colors" title="Edit">
                              <span className="material-symbols-outlined text-lg leading-none">edit</span>
                            </button>
                            <button onClick={() => setDeleteId(vendor.id)} className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors" title="Delete">
                              <span className="material-symbols-outlined text-lg leading-none">delete</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile SwipeableRow list */}
              <div className="sm:hidden divide-y divide-gray-50">
                {visibleVendors.map(vendor => {
                  const rowActions: SwipeAction[] = [
                    {
                      key: 'edit',
                      label: 'Edit',
                      icon: 'edit',
                      variant: 'default',
                      onClick: () => setEditVendor(vendor),
                    },
                    {
                      key: 'delete',
                      label: 'Delete',
                      icon: 'delete',
                      variant: 'danger',
                      onClick: () => setDeleteId(vendor.id),
                    },
                  ];
                  return (
                    <SwipeableRow key={vendor.id} actions={rowActions}>
                      <div className="px-4 py-3.5 cursor-pointer" onClick={() => navigate(`/vendors/${vendor.id}`)}>
                        <p className="font-medium text-gray-900">{vendor.name}</p>
                        {vendor.contact && <p className="text-xs text-gray-500 mt-0.5">{vendor.contact}</p>}
                        {vendor.phone && <p className="text-xs text-gray-400">{vendor.phone}</p>}
                      </div>
                    </SwipeableRow>
                  );
                })}
              </div>
            </PullToRefresh>
          )}
        </div>
      </div>

      {/* Edit modal */}
      {editVendor && (
        <EditModal
          vendor={editVendor}
          onSave={handleEditSave}
          onClose={() => setEditVendor(null)}
        />
      )}

      {/* Delete confirm */}
      {deleteId !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={e => { if (e.target === e.currentTarget && !deleting) setDeleteId(null); }}
        >
          <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl p-6 space-y-4">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 text-red-600">
                <span className="material-symbols-outlined text-xl">delete</span>
              </span>
              <h2 className="text-base font-bold text-gray-900">Delete Vendor</h2>
            </div>
            <p className="text-sm text-gray-600">
              Are you sure you want to delete this vendor? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteId(null)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteId)}
                disabled={deleting}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-60 transition-colors"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </MainLayout>
  );
};
